import { SearchParameters } from "@notionhq/client/build/src/api-endpoints";
import { supabase } from "../config/supabase";
import { normalizeRecordMap } from "./normalizeRecordMap";

type NotionRecordMap = {
  pageId: string;
  publicUrl: string | null;
};

export interface NotionRequestOptions {
  /** Serve from (and populate) the shared request cache. Off by default. */
  cache?: boolean;
}

/**
 * Opt-in cache for the two heavy Notion reads: a page's WHOLE recordMap and a
 * WHOLE database query.
 *
 * Expanding a node downloads its entire owning page (or database) just to keep
 * one child level, so expanding 12 siblings of one page downloaded that page 12
 * times. The CPU for those expands is ~12ms — the wait is almost entirely
 * redundant network.
 *
 * What is stored is the IN-FLIGHT PROMISE, not the resolved value, so N
 * simultaneous chip clicks collapse into ONE request rather than merely N
 * sequential ones deduping after the first returns.
 *
 * Caching is opt-in because refresh deliberately re-reads Notion to pick up
 * edits; serving it a cached page would silently show stale content. Only the
 * expand paths pass `{ cache: true }`.
 *
 * A hit resolves to the same value the miss produced — normalization runs INSIDE
 * the cached function, not around it — and that value is shared, never copied:
 * every consumer (the renderer, the tree builders) treats a recordMap as
 * read-only.
 */
const requestCache = new Map<string, CachedRequest>();

const REQUEST_CACHE_TTL_MS = 60_000;
const REQUEST_CACHE_MAX_ENTRIES = 20;

interface CachedRequest {
  promise: Promise<unknown>;
  expiresAt: number;
}

/** supabase-js reports failures in `error` rather than by throwing. */
const isFailure = (res: unknown): boolean =>
  typeof res === "object" &&
  res !== null &&
  Boolean((res as { error?: unknown }).error);

/** Drop `key` only if it still holds `entry` (never evict a newer request). */
const dropIfCurrent = (key: string, entry: CachedRequest): void => {
  if (requestCache.get(key) === entry) {
    requestCache.delete(key);
  }
};

const withCache = <T>(
  key: string,
  run: () => Promise<T>,
  options?: NotionRequestOptions,
): Promise<T> => {
  if (!options?.cache) {
    return run();
  }

  const now = Date.now();
  const hit = requestCache.get(key);
  if (hit && hit.expiresAt > now) {
    return hit.promise as Promise<T>;
  }
  requestCache.delete(key);

  const pending = run();
  const entry: CachedRequest = {
    // Failures are never cached, so a retry actually retries.
    promise: pending.then(
      (res) => {
        if (isFailure(res)) {
          dropIfCurrent(key, entry);
        }
        return res;
      },
      (err) => {
        dropIfCurrent(key, entry);
        throw err;
      },
    ),
    expiresAt: now + REQUEST_CACHE_TTL_MS,
  };
  requestCache.set(key, entry);

  // Bounded so a long session can't grow it without limit; Map iterates in
  // insertion order, so the first key is the oldest.
  while (requestCache.size > REQUEST_CACHE_MAX_ENTRIES) {
    const oldest = requestCache.keys().next();
    if (oldest.done) {
      break;
    }
    requestCache.delete(oldest.value);
  }

  return entry.promise as Promise<T>;
};

/** Forget every cached response (account switch, tests). */
export const clearNotionRequestCache = (): void => {
  requestCache.clear();
};

/**
 * `fetch_all` is ours, not Notion's: the edge function reads it, strips it, and
 * pages through `has_more` instead of returning only the first 100 results. It
 * is declared here rather than cast away at the call site so `page_size` and
 * `sort` keep their real types.
 */
export const notionSearch = (
  query: SearchParameters & { fetch_all?: boolean },
) => {
  return supabase.functions.invoke("notion-integration", {
    method: "POST",
    body: query,
  });
};

// Ids are interpolated into a URL, so they are percent-encoded: an id carrying
// `&`, `#` or `?` would otherwise inject extra query params into the request.
export const notionPage = (pageId: string) => {
  return supabase.functions.invoke(
    `notion-integration?pageId=${encodeURIComponent(pageId)}`,
    {
      method: "GET",
    },
  );
};

export const notionDatabase = (
  databaseId: string,
  options?: NotionRequestOptions,
) => {
  return withCache(
    `db:${databaseId}`,
    () =>
      supabase.functions.invoke(
        `notion-collection?databaseId=${encodeURIComponent(databaseId)}`,
        {
          method: "GET",
        },
      ),
    options,
  );
};

export interface NotionOutlineRequest {
  /** The page or block whose children to list. */
  blockId: string;
  /** How many levels to resolve. 1 = this level only; 2 also prefetches. */
  depth: number;
  /** Ask for the root's own title + icon (generation only). */
  withTitle?: boolean;
}

/**
 * One level of a page's block outline, via the OFFICIAL Notion API.
 *
 * This is the cheap read that replaced fetching a whole page's recordMap to
 * paint six pills: generation needs the root plus level 1, an expand needs one
 * level, and `blocks.children.list` answers exactly that in one request. The
 * recordMap route stays for the side peek (react-notion-x needs that shape) and
 * as this route's fallback.
 *
 * Almost always called with `{ cache: true }`: generation prefetches depth 2 in
 * the background, and every expand of a level-1 node is then served from that
 * in-flight or resolved promise rather than a new round trip.
 */
export const notionOutline = (
  request: NotionOutlineRequest,
  options?: NotionRequestOptions,
) => {
  const withTitle = request.withTitle === true;
  return withCache(
    `outline:${request.blockId}:${request.depth}:${withTitle ? 1 : 0}`,
    () =>
      supabase.functions.invoke(
        `notion-outline?blockId=${encodeURIComponent(request.blockId)}&depth=${
          request.depth
        }${withTitle ? "&withTitle=1" : ""}`,
        { method: "GET" },
      ),
    options,
  );
};

// Notion is migrating its record entries to `{ value: { value } }`. react-notion-x
// reads the flat `{ value }`, so an un-normalized recordMap makes <Block> throw
// and takes the app down. Normalize here, at the single point the recordMap
// enters the app, so no consumer has to know about the two shapes.
export const notionPageRecordMap = (
  data: NotionRecordMap,
  options?: NotionRequestOptions,
) => {
  // publicUrl is part of the request, so it is part of the identity.
  return withCache(
    `page:${data.pageId}|${data.publicUrl ?? ""}`,
    async () => {
      const res = await supabase.functions.invoke(`notion-recordmap`, {
        method: "POST",
        body: data,
      });

      return res.error ? res : { ...res, data: normalizeRecordMap(res.data) };
    },
    options,
  );
};

export const createPage = (data: any) => {
  return supabase.functions.invoke(
    `create-notion?pageId=${encodeURIComponent(data.pageId)}`,
    {
      method: "POST",
      body: {
        pageTitle: data.pageTitle,
        type: data.type,
      },
    },
  );
};
